import { PresentationType, MonetizationType } from '@/@types/graphql-types';
import { TitleObjectType } from '@/interfaces/titles';

// import { getVm } from '@/helpers/vm-helper';
// const vm = getVm();

// UTIL
type valueof<T> = T[keyof T];

interface SponsoredRecommendationData {
	id: string;
	name: string;
	countries: Array<string>;
	start_date: number;
	end_date: number;
	provider_id: number;
	genres: string[];
	type: SponsoredRecommendationType;
	promotion: SponsoredRecommendationPromotion;
	content: {
		id: string;
		objectType: TitleObjectType;
		objectId: number;
		watchNowOffer: {
			[key: string]: {
				/** @Warning Do not use a link that starts with click.justwatch.com or it will get double affiliated! */
				standardWebURL: string;
				currency: string;
				package: {
					clearName: string;
					packageId: number;
				};
				monetizationType: MonetizationType;
				presentationType: PresentationType;
			};
		};
	};
	overrideBuyboxClickout: boolean;
	hideRating: boolean;
	watchnowLabel?: string;
	/** Enter the backdrop image name WITHOUT its format type (e.g: without .png) */
	backdropImageName: string;
	impressionTagInternal: string;
	impressionTagExternal: string;
}

type SponsoredRecommendationType = valueof<typeof SponsoredRecommendationType>;
const SponsoredRecommendationType = {
	Regular: 'REGULAR',
	Cinematic: 'CINEMATIC',
} as const;

type SponsoredRecommendationPromotion = valueof<typeof SponsoredRecommendationPromotion>;
const SponsoredRecommendationPromotion = {
	/** Sponsored Recommendation in SimilarTitles at bottom of TitleDetail */
	None: 'NONE',

	/** Sponsored Recommendation in SimilarTitles below BuyBox */
	Low: 'LOW',

	/** Sponsored Recommendation in SimilarTitles above Buybox */
	High: 'HIGH',
} as const;

// List of current Sponsored Recommendations:
const sponsoredRecommendations: Array<SponsoredRecommendationData> = [
	{
		id: '8faa4803-043f-4c40-907d-923f20cc52bd',
		name: 'The Crowded Room',
		countries: ['US'],
		start_date: new Date('2023-06-15').setUTCHours(0, 0, 0, 0),
		end_date: new Date('2023-06-20').setUTCHours(23, 59, 59, 999),
		provider_id: 350, // APPLE TV PLUS
		genres: ['drm'], // Drama
		type: SponsoredRecommendationType.Regular,
		promotion: SponsoredRecommendationPromotion.None,
		content: {
			id: 'ts288696', // The Crowded Room
			objectType: TitleObjectType.SHOW,
			objectId: 288696, // The Crowded Room
			watchNowOffer: {
				US: {
					standardWebURL:
						'https://click.justwatch.com/a?r=https%3A%2F%2Ftv.apple.com%2Fus%2Fepisode%2Fexodus%2Fumc.cmc.q2un5rf5n00t4ij1ovx9soo%3Fat%3D1000l3V2%26ct%3Dapp_tvplus%26playableId%3Dtvs.sbd.4000%253AA0010901002%26showId%3Dumc.cmc.495pmjg441gg3j70j3x2yufx9&cx=eyJzY2hlbWEiOiJpZ2x1OmNvbS5zbm93cGxvd2FuYWx5dGljcy5zbm93cGxvdy9jb250ZXh0cy9qc29uc2NoZW1hLzEtMC0wIiwiZGF0YSI6W3sic2NoZW1hIjoiaWdsdTpjb20uanVzdHdhdGNoL3RpdGxlX2NvbnRleHQvanNvbnNjaGVtYS8xLTEtMCIsImRhdGEiOnsidGl0bGVJZCI6Mjg4Njk2LCJvYmplY3RUeXBlIjoic2hvdyIsImp3RW50aXR5SWQiOiJ0czI4ODY5NiJ9fSx7InNjaGVtYSI6ImlnbHU6Y29tLmp1c3R3YXRjaC9jbGlja291dF9jb250ZXh0L2pzb25zY2hlbWEvMS0yLTAiLCJkYXRhIjp7InByb3ZpZGVyIjoiQXBwbGUgVFYgUGx1cyIsInByb3ZpZGVySWQiOjM1MCwibW9uZXRpemF0aW9uVHlwZSI6ImZsYXRyYXRlIiwicHJlc2VudGF0aW9uVHlwZSI6IjRrIiwiY3VycmVuY3kiOiJVU0QiLCJwYXJ0bmVySWQiOjF9fV19&uct_country=US&uct_buybox=normal&uct_sport=&sid=&uct_ct=',
					currency: 'EUR',
					package: {
						clearName: 'Apple TV Plus',
						packageId: 350, // WOW
					},
					monetizationType: MonetizationType.Flatrate,
					presentationType: PresentationType.Hd,
				},
			},
		},
		overrideBuyboxClickout: true,
		hideRating: false, // Hides IMDb rating on SR poster if true
		watchnowLabel: undefined,
		backdropImageName: 'apple-tv-plus-the-crowded-room', // Enter the backdrop image name WITHOUT its format type (e.g: without .png)
		impressionTagInternal: '',
		impressionTagExternal: '',
	},
	{
		id: 'a8edce64-b739-4dff-8168-7a9a75f3ccc4',
		name: 'And Just Like That',
		countries: ['DE'],
		start_date: new Date('2023-06-13').setUTCHours(0, 0, 0, 0),
		end_date: new Date('2023-06-30').setUTCHours(23, 59, 59, 999),
		provider_id: 30, // WOW
		genres: ['drm', 'rma', 'cmy'], // Drama, Romance, Comedy
		type: SponsoredRecommendationType.Regular,
		promotion: SponsoredRecommendationPromotion.None,
		content: {
			id: 'ts278961', // And Just Like That
			objectType: TitleObjectType.SHOW,
			objectId: 278961, // And Just Like That
			watchNowOffer: {
				DE: {
					standardWebURL:
						'https://ad.doubleclick.net/ddm/trackclk/N1111979.2621703JUSTWATCHGMBH/B30092304.368303318;dc_trk_aid=559624489;dc_trk_cid=193476282;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;ltd=;dc_tdv=1',
					currency: 'EUR',
					package: {
						clearName: 'WOW',
						packageId: 30, // WOW
					},
					monetizationType: MonetizationType.Flatrate,
					presentationType: PresentationType.Hd,
				},
			},
		},
		overrideBuyboxClickout: true,
		hideRating: true, // Hides IMDb rating on SR poster if true
		watchnowLabel: undefined,
		backdropImageName: 'wow-and_just_like_that-4', // Enter the backdrop image name WITHOUT its format type (e.g: without .png)
		impressionTagInternal: '',
		impressionTagExternal:
			'https://ad.doubleclick.net/ddm/trackimp/N1111979.2621703JUSTWATCHGMBH/B30092304.368303318;dc_trk_aid=559624489;dc_trk_cid=193476282;ord=[timestamp];dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;gdpr=${GDPR};gdpr_consent=${GDPR_CONSENT_755};ltd=;dc_tdv=1?',
	},
	{
		id: '13ca7865-866d-475f-afb5-9a6d79df9225',
		name: "Tom Clancy's Jack Ryan",
		countries: ['ES'],
		start_date: new Date('2023-06-30').setUTCHours(0, 0, 0, 0),
		end_date: new Date('2023-07-03').setUTCHours(23, 59, 59, 999),
		provider_id: 119, // Amazon Prime Video
		genres: ['act', 'crm'], // Action & Adventure, Crime
		type: SponsoredRecommendationType.Regular,
		promotion: SponsoredRecommendationPromotion.None,
		content: {
			id: 'ts75062', // Tom Clancy's Jack Ryan
			objectType: TitleObjectType.SHOW,
			objectId: 75062, // Tom Clancy's Jack Ryan
			watchNowOffer: {
				ES: {
					standardWebURL:
						'https://app.primevideo.com/detail?gti=amzn1.dv.gti.56b2af4a-833b-5d9e-32be-368db8e46fab',
					currency: 'EUR',
					package: {
						clearName: 'Amazon Prime Video',
						packageId: 119, // Amazon Prime Video
					},
					monetizationType: MonetizationType.Flatrate,
					presentationType: PresentationType.Hd,
				},
			},
		},
		overrideBuyboxClickout: false,
		hideRating: false, // Hides IMDb rating on SR poster if true
		watchnowLabel: undefined,
		backdropImageName: 'amazon-jack_ryan-1', // Enter the backdrop image name WITHOUT its format type (e.g: without .png)
		impressionTagInternal: '',
		impressionTagExternal: '',
	},
	{
		id: 'f6addf13-8422-441a-b5ca-d5953c8602a5',
		name: 'Hijack',
		countries: ['GB', 'FR', 'CA'],
		start_date: new Date('2023-06-30').setUTCHours(0, 0, 0, 0),
		end_date: new Date('2023-07-03').setUTCHours(23, 59, 59, 999),
		provider_id: 350, // AppleTV+
		genres: ['trl'], // Thriller
		type: SponsoredRecommendationType.Regular,
		promotion: SponsoredRecommendationPromotion.None,
		content: {
			id: 'ts346878', // Hijack
			objectType: TitleObjectType.SHOW,
			objectId: 346878, // Hijack
			watchNowOffer: {
				GB: {
					standardWebURL:
						'https://tv.apple.com/gb/episode/final-call/umc.cmc.4levibvvz01hl4zsm0jdk5v2p?at=1000l3V2&ct=app_tvplus&playableId=tvs.sbd.4000%3AA0059301001&showId=umc.cmc.1dg08zn0g3zx52hs8npoj5qe3',
					currency: 'GBP',
					package: {
						clearName: 'AppleTV+',
						packageId: 350, // AppleTV+
					},
					monetizationType: MonetizationType.Flatrate,
					presentationType: PresentationType.Hd,
				},
				FR: {
					standardWebURL:
						'https://tv.apple.com/fr/episode/final-call/umc.cmc.4levibvvz01hl4zsm0jdk5v2p?at=1000l3V2&ct=app_tvplus&playableId=tvs.sbd.4000%3AA0059301001&showId=umc.cmc.1dg08zn0g3zx52hs8npoj5qe3',
					currency: 'EUR',
					package: {
						clearName: 'AppleTV+',
						packageId: 350, // AppleTV+
					},
					monetizationType: MonetizationType.Flatrate,
					presentationType: PresentationType.Hd,
				},
				CA: {
					standardWebURL:
						'https://tv.apple.com/ca/episode/final-call/umc.cmc.4levibvvz01hl4zsm0jdk5v2p?at=1000l3V2&ct=app_tvplus&playableId=tvs.sbd.4000%3AA0059301001&showId=umc.cmc.1dg08zn0g3zx52hs8npoj5qe3',
					currency: 'CAD',
					package: {
						clearName: 'AppleTV+',
						packageId: 350, // AppleTV+
					},
					monetizationType: MonetizationType.Flatrate,
					presentationType: PresentationType.Hd,
				},
			},
		},
		overrideBuyboxClickout: false,
		hideRating: false, // Hides IMDb rating on SR poster if true
		watchnowLabel: undefined,
		backdropImageName: 'apple-hijack-1', // Enter the backdrop image name WITHOUT its format type (e.g: without .png)
		impressionTagInternal: '',
		impressionTagExternal: '',
	},
];

const customSponsoredRecommendations: Array<any> = [
	// {
	// 	id: 'a41049ba-0775-4a43-8923-9af4f97df32d',
	// 	name: 'JustWatch Favorites',
	// 	countries: [
	// 		'US',
	// 		'UK',
	// 		'AU',
	// 		'NZ',
	// 		'IE',
	// 		'IN',
	// 		'ZA',
	// 		'NG',
	// 		'CA',
	// 		'KE',
	// 		'AE',
	// 		'SA',
	// 		'BA',
	// 		'QA',
	// 		'EG',
	// 		'PL',
	// 		'NL',
	// 		'BE',
	// 		'IT',
	// 		'FR',
	// 		'CH',
	// 		'DZ',
	// 		'ES',
	// 		'MX',
	// 		'AR',
	// 		'DE',
	// 		'AT',
	// 		'PT',
	// 		'BR',
	// 		'TR',
	// 	],
	// 	start_date: new Date('2023-04-07').setUTCHours(0, 0, 0, 0),
	// 	end_date: new Date('2023-04-10').setUTCHours(23, 59, 59, 999),
	// 	title: 'JustWatch Favorites',
	// 	text: vm.$tc('WEBAPP_LIST_JWFAVORITES_CONTEXT'),
	// 	fullPath: 'https://justwatch.com/shared?id=tl-tu-4ab579f5-b3e7-4fb5-8c9d-1032cd7befd3',
	// 	backdropImageName: 'jw_favorites-2',
	// },
];

const getSponsoredRecommendations = (country: string) => {
	const now = new Date().getTime();
	return sponsoredRecommendations.filter((sr: SponsoredRecommendationData) => {
		return sr.countries.includes(country) && sr.start_date <= now && now <= sr.end_date;
	});
};

const getCustomSponsoredRecommendations = (country: string) => {
	const now = new Date().getTime();
	return customSponsoredRecommendations.filter((sr: any) => {
		return sr.countries.includes(country) && sr.start_date <= now && now <= sr.end_date;
	});
};

const sponsoredRecommendationProviders = () => {
	return sponsoredRecommendations.map((sr: SponsoredRecommendationData) => {
		return sr.provider_id;
	});
};

const isSponsoredRecommendationTitle = (id: string | undefined, country: string) => {
	const now = new Date().getTime();
	return !!sponsoredRecommendations.find((sr: SponsoredRecommendationData) => {
		return sr.content.id === id && sr.countries.includes(country) && sr.start_date <= now && now <= sr.end_date;
	});
};

const showSponsoredRecommendations = (country: string) => {
	const now = new Date().getTime();
	return (
		!!sponsoredRecommendations.find((sr: SponsoredRecommendationData) => {
			return sr.countries.includes(country) && sr.start_date <= now && now <= sr.end_date;
		}) ||
		!!customSponsoredRecommendations.find((sr: any) => {
			return sr.countries.includes(country) && sr.start_date <= now && now <= sr.end_date;
		})
	);
};

export {
	SponsoredRecommendationData,
	sponsoredRecommendations,
	customSponsoredRecommendations,
	getSponsoredRecommendations,
	getCustomSponsoredRecommendations,
	sponsoredRecommendationProviders,
	isSponsoredRecommendationTitle,
	showSponsoredRecommendations,
	SponsoredRecommendationPromotion,
	SponsoredRecommendationType,
};
